import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

export type PccAllergyCategory = 'Drug' | 'Food' | 'Environmental' | 'Substance' | 'Other';
export type PccAllergyClinicalStatus = 'active' | 'priorHistory' | 'resolved';
export class PccAllergyParams {
	categories?: PccAllergyCategory[];

	clinicalStatuses?: PccAllergyClinicalStatus[];
}

/**
 * PCC Allergy Information
 *
 * Allergies include detailed information on the category, type, reaction type, reaction, and its status.
 *
 * SimpliScreen maps the allergy to the following string for embedding in the RAG:
 * Allergy: {allergen}, Status: {status}, Category: {category}, Type: {type}, Severity: {severity}, Onset Date: {onsetDate}, Resolved Date: {resolvedDate}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/allergyintolerances?patientId={patientId}
 */
export class PccAllergy extends PccAuditInfo {
	/**
	 * The allergen that the patient is allergic to.
	 */
	allergen: string;

	/**
	 * The category of the allergy.
	 */
	category: PccAllergyCategory;

	/**
	 * The unique identifier for the allergy.
	 */
	allergyIntoleranceId: number;

	/**
	 * The clinical status of the allergen.
	 */
	clinicalStatus: PccAllergyClinicalStatus;

	/**
	 * Represents the date when the allergy was identified. The date is expressed as YYYY-MM-DD.
	 */
	onsetDate: string;

	/**
	 * PointClickCare's unique identifier for a patient within the organization.
	 *
	 * When a patient is admitted to a facility within an organization, they are assigned a patientId which is unique to their stay within that facility. If the same patient were to get admitted to another facility within the same organization in the future, they would be assigned a new patientId. The patientId however is unique at the organization level. That is, no two patients across different facilities within the organization will have the same patientId.
	 *
	 * This identifier is crucial for tracking patient information and care but must not be displayed in any user interface due to legal privacy requirements.
	 */
	patientId: number;

	/**
	 * The date the allergy was last updated.
	 */
	lastUpdatedDateTime: string;

	/**
	 * Represents each reaction.
	 */
	reaction: Array<{
		/**
		 * List of reaction manifestations. reaction.manifestations may not be enabled.
		 */
		manifestations: Array<{
			/**
			 * UI Friendly name for the coding system of this entry. (e.g. SNOMED CT, RxNorm, PCC)..
			 */
			systemName: string;
			/**
			 * The name space to search to find the code supplied for mapping between coding systems. (e.g. OID, URL, PCC category). Optional if systemName is "PCC".
			 */
			systemNameSpace: string;
			/**
			 * Represents the code value within the coding system. Optional if systemName is "PCC".
			 */
			code: string;
			/**
			 * Represents the displayable name of the entry (E.g. Allergen- Penicillin, Apple, Mold, Reaction type- Photosensitivity reactions, Severity- Mild).
			 */
			displayName: string;
		}>;

		/**
		 * The severity of the reaction.
		 */
		severity: string;
	}>;

	/**
	 * Free text notes associated to the allergy entered by the user. Notes can be added at the time of adding an allergy or after an allergy has been added.
	 */
	reactionNote: string;

	/**
	 * reactionSubType
	 * string
	 *
	 * Represents the reaction sub type. Each reaction type can have its own sub type.
	 *
	 * Reaction sub type for 'Anaphylaxis'
	 * - Bronchospasm
	 * - Hypotension
	 * - Laryngeal edema

	 * Reaction sub type for 'Cutaneous reactions'
	 * - Contact sensitivity
	 * - Erythema multiforme
	 * - Erythema nodosum
	 * - Exfoliative dermatitis
	 * - Fixed drug eruption
	 * - Maculopapular (morbilliform) rash
	 * - Pruritus
	 * - Stevens-Johnson syndrome
	 * - Toxic epidermal necrolysis
	 * - Urticaria and angioedema
	 * - Vasculitis
	 *
	 * Reaction sub type for 'Hepatic reactions'
	 * - Cholestatic reactions
	 * - Hepatocellular damage
	 *
	 * Reaction sub type for 'Photosensitivity reactions'
	 * - Destruction of formed elements of blood
	 * - Hemolytic anemia
	 * - Neutropenia
	 * - Thrombocytopenia
	 *
	 * Reaction sub type for 'Pulmonary reactions'
	 * - Edema
	 * - Fibrosis
	 * - Interstitial or alveolar pneumonitis
	 *
	 * Reaction sub type for 'Renal reactions'
	 * - Glomerulonephritis
	 * - Interstitial nephritis
	 * - Nephrotic syndrome
	 */
	reactionSubType: string;

	/**
	 * The type of reaction.
	 */
	reactionType: 'Anaphylaxis' | 'Cutaneous reactions' | 'Drug Fever' | 'Hepatic reactions' | 'Lymphadenopathy' | 'Nausea' | 'Photosensitivity reactions' | 'Pulmonary reactions' | 'Renal reactions' | 'Serum sickness' | 'Systemic vasculitis';

	/**
	 * Represents the severity of the allergy for the patient. 'Unknown' is returned if no value for 'Severity' is selected when entering the allergy through the PointClickCare application.
	 */
	severity: 'Mild' | 'Moderate' | 'Severe' | 'Unknown';

	/**
	 * Represents the allergy type. 'Type' was not always a required field in the PointClickCare application when adding an allergy. There may be existing older records where this value was not set. In those cases, a value of 'Unknown' will be returned.
	 */
	type: 'Allergy' | 'Intolerance' | 'Propensity to Adverse Reactions' | 'Unknown';

	/**
	 * uscdiV1Category may not be enabled.
	 */
	uscdiV1Category: string;

	/**
	 * Represents the details of the allergy.
	 *
	 * SimpliScreen maps the allergy to the following string for embedding in the RAG:
	 * Allergy: {allergen}, Status: {clinicalStatus}, Category: {category}, Type: {type}, Severity: {severity}, Onset Date: {onsetDate}, Resolved Date: {resolvedDate}
	 *
	 */
	details: string;

	/**
	 * Represents if the patient has no known allergies or whether if the allergies for the patient are yet to be determined. Possible values are 'No Known Allergies' or 'To Be Determined'.
	 */
	outliers?: 'No Known Allergies' | 'To Be Determined' | '' = '';

	constructor(partial?: Partial<PccAllergy>) {
		super();
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails() {
		if (this.outliers) return this.outliers;
		const details = [`Data Type: Allergy`];
		if (this.allergen) details.push(`Allergy: ${this.allergen}`);
		if (this.clinicalStatus) details.push(`Status: ${this.clinicalStatus}`);
		if (this.category) details.push(`Category: ${this.category}`);
		if (this.type) details.push(`Type: ${this.type}`);
		if (this.severity) details.push(`Severity: ${this.severity}`);
		if (this.onsetDate) details.push(`Onset Date: ${this.onsetDate}`);
		if (this.resolvedDate) details.push(`Resolved Date: ${this.resolvedDate}`);
		return details.join(', ');
	}
}
